/**
 * API Service Layer
 * Handles all HTTP requests to the backend API
 */

import type {
  LoginResponse,
  RegisterResponse,
  TokenRefreshResponse,
  PropertyListResponse,
  PropertyDetailResponse,
  PropertyCreateInput,
  PropertyUpdateInput,
  TaskListResponse,
  TaskDetailResponse,
  TaskCreateInput,
  TaskUpdateInput,
  CalendarEventListResponse,
  PropertyCalendarListResponse,
  CalendarAddInput,
  CalendarUpdateInput,
  BlockDatesInput,
  CleaningSessionListResponse,
  CleaningSessionDetailResponse,
  CleaningPhotoResponse,
  ExpenseListResponse,
  ExpenseDetailResponse,
  ExpenseCreateInput,
  ExpenseUpdateInput,
  ExpenseCategoriesResponse,
  RevenueListResponse,
  RevenueCreateInput,
  RevenueSummaryResponse,
  InvoiceListResponse,
  InvoiceDetailResponse,
  InvoiceCreateInput,
  InvoiceUpdateInput,
  InvoicePaymentListResponse,
  InvoicePaymentInput,
  InventoryCatalogListResponse,
  InventoryCatalogDetailResponse,
  InventoryCatalogCreateInput,
  InventoryCatalogUpdateInput,
  InventoryItemListResponse,
  InventoryItemDetailResponse,
  InventoryItemCreateInput,
  InventoryItemUpdateInput,
  GuidebookResponse,
  GuidebookCreateInput,
  GuidebookUpdateInput,
  AccessCodeListResponse,
  AccessCodeCreateInput,
  AccessCodeCreateResponse,
  RecommendationListResponse,
  RecommendationCreateInput,
  MessageListResponse,
  MessageSendInput,
  MessageTemplateListResponse,
  MessageTemplateCreateInput,
  SmsSendInput,
  SmsSendResponse,
  TurnoverListResponse,
  TurnoverDetailResponse,
  TurnoverUpdateInput,
  SuccessResponse,
  DeleteResponse,
  ImageUploadResponse,
} from '@/types';
import type {
  AddCaseEventInput,
  CaseEvent,
  CaseEvidence,
  CaseStayOption,
  CheckoutCheck,
  CreateCheckoutCheckInput,
  CreateCheckoutCheckResponse,
  CreateGuestCaseInput,
  CreateGuestCaseResponse,
  EvidenceCandidatesResponse,
  GuestCase,
  GuestCaseDetail,
  GuestCaseListFilters,
  GuestCaseListResponse,
  PinEvidenceInput,
  RecentStaysResult,
  StayLookupResponse,
  UpdateGuestCaseInput,
} from '@/types/guest-cases';
import type { CalendarEventRow } from '@/types/database';
import type {
  OperationsAttachment, OperationsBrief, OperationsChecklistItem, OperationsEvent, OperationsGrant, OperationsProperty,
  OperationsPolicy, OperationsPolicyEvent, OperationsRequest, OperationsRole,
  OperationsInvoiceSource, OperationsInvoiceLink,
} from '@/types/operations';
import { BLOCKED_STATUS, shiftDays } from '@/lib/guestCases';
import { getToday } from '@/utils/dates';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  public extra?: Record<string, unknown>;
  constructor(public status: number, message: string, extra?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.extra = extra;
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');

  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    // A 401 on any non-auth endpoint means our stored token is bad (expired,
    // revoked, or signed with a rotated secret). Clear it and bounce to login
    // instead of leaving the user on a broken page full of 401s. Auth endpoints
    // (/auth/login, /auth/refresh, ...) own their own 401 semantics, so skip them.
    if (response.status === 401 && !endpoint.startsWith('/auth/')) {
      localStorage.removeItem('auth_token');
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    // Keep the machine-readable parts of an error body (which field was
    // refused, why a write conflicted) so forms can point at the cause.
    const extra: Record<string, unknown> = {};
    for (const key of ['field', 'reason', 'event_id'] as const) {
      if (data && data[key] !== undefined && data[key] !== null) extra[key] = data[key];
    }
    throw new ApiError(
      response.status,
      data.error || data.message || 'An error occurred',
      Object.keys(extra).length ? extra : undefined,
    );
  }

  return data;
}

// Authentication API
export const authApi = {
  async login(email: string, password: string) {
    const data = await fetchApi<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
    }
    return data;
  },

  async register(userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role?: string;
  }) {
    const data = await fetchApi<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
    }
    return data;
  },

  async logout() {
    await fetchApi<SuccessResponse>('/auth/logout', { method: 'POST' });
    localStorage.removeItem('auth_token');
  },

  async refreshToken() {
    const data = await fetchApi<TokenRefreshResponse>('/auth/refresh', { method: 'POST' });
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
    }
    return data;
  },

  async sendVerificationEmail() {
    return fetchApi<SuccessResponse>('/auth/send-verification', { method: 'POST' });
  },

  async verifyEmail(token: string) {
    return fetchApi<SuccessResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async requestPasswordReset(email: string) {
    return fetchApi<SuccessResponse>('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(token: string, password: string) {
    return fetchApi<SuccessResponse>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },
};

// Properties API
export const propertiesApi = {
  async list() {
    return fetchApi<PropertyListResponse>('/properties');
  },

  async get(id: string) {
    return fetchApi<PropertyDetailResponse>(`/properties/${id}`);
  },

  async create(propertyData: PropertyCreateInput) {
    return fetchApi<PropertyDetailResponse>('/properties', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  },

  async update(id: string, propertyData: PropertyUpdateInput) {
    return fetchApi<PropertyDetailResponse>(`/properties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(propertyData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/properties/${id}`, {
      method: 'DELETE',
    });
  },

  async duplicate(id: string) {
    return fetchApi<PropertyDetailResponse>(`/properties/${id}/duplicate`, {
      method: 'POST',
    });
  },

  async checkSlug(slug: string, excludeId?: string) {
    const params = new URLSearchParams({ slug });
    if (excludeId) params.append('exclude', excludeId);
    return fetchApi<{ available: boolean; slug: string; reason?: string }>(
      `/properties/check-slug?${params.toString()}`
    );
  },

  async uploadImage(propertyId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('property_id', propertyId);

    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_BASE_URL}/upload/property-image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data: ImageUploadResponse = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as unknown as { error: string }).error || 'Upload failed');
    }
    return data;
  },
};

// Tasks API
export const tasksApi = {
  async list(filters?: { status?: string; property_id?: string }) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.property_id) params.append('property_id', filters.property_id);

    const queryString = params.toString();
    return fetchApi<TaskListResponse>(`/tasks${queryString ? `?${queryString}` : ''}`);
  },

  async create(taskData: TaskCreateInput) {
    return fetchApi<TaskDetailResponse>('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  },

  async get(id: string) {
    return fetchApi<TaskDetailResponse>(`/tasks/${id}`);
  },

  async update(id: string, taskData: TaskUpdateInput) {
    return fetchApi<TaskDetailResponse>(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(taskData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/tasks/${id}`, {
      method: 'DELETE',
    });
  },
};

// Calendar API
export const calendarApi = {
  async getEvents(propertyId: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams({ property_id: propertyId });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return fetchApi<CalendarEventListResponse>(`/calendar/events?${params.toString()}`);
  },

  async syncCalendar(propertyId: string) {
    return fetchApi<SuccessResponse>('/calendar/sync', {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId }),
    });
  },

  // Property calendar management
  async listPropertyCalendars(propertyId: string) {
    return fetchApi<PropertyCalendarListResponse>(`/properties/${propertyId}/calendars`);
  },

  async addPropertyCalendar(propertyId: string, data: CalendarAddInput) {
    return fetchApi<SuccessResponse>(`/properties/${propertyId}/calendars`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updatePropertyCalendar(propertyId: string, calendarId: string, data: CalendarUpdateInput) {
    return fetchApi<SuccessResponse>(`/properties/${propertyId}/calendars/${calendarId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deletePropertyCalendar(propertyId: string, calendarId: string) {
    return fetchApi<DeleteResponse>(`/properties/${propertyId}/calendars/${calendarId}`, {
      method: 'DELETE',
    });
  },

  // Get iCal feed URL for property
  getICalFeedUrl(propertyId: string): string {
    return `${window.location.origin}/api/properties/${propertyId}/calendar.ics`;
  },

  // Block dates on property calendar
  async blockDates(propertyId: string, data: BlockDatesInput) {
    return fetchApi<SuccessResponse>(`/properties/${propertyId}/block-dates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteEvent(eventId: string | number, options?: { series?: boolean }) {
    const qs = options?.series ? '?series=true' : '';
    return fetchApi<{ success: boolean; message: string; deleted_count: number }>(
      `/calendar/events/${eventId}${qs}`,
      { method: 'DELETE' }
    );
  },
};

// Cleaning Sessions API
export const cleaningApi = {
  async list(filters?: {
    property_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    return fetchApi<CleaningSessionListResponse>(`/cleaning/sessions${queryString ? `?${queryString}` : ''}`);
  },

  async start(propertyId: string, notes?: string) {
    return fetchApi<CleaningSessionDetailResponse>('/cleaning/sessions', {
      method: 'POST',
      body: JSON.stringify({ property_id: propertyId, notes }),
    });
  },

  async get(sessionId: string) {
    return fetchApi<CleaningSessionDetailResponse>(`/cleaning/sessions/${sessionId}`);
  },

  async update(sessionId: string, updates: { notes?: string; status?: string }) {
    return fetchApi<CleaningSessionDetailResponse>(`/cleaning/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async complete(sessionId: string, notes?: string) {
    return fetchApi<CleaningSessionDetailResponse>(`/cleaning/sessions/${sessionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  async delete(sessionId: string) {
    return fetchApi<DeleteResponse>(`/cleaning/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  async uploadPhoto(sessionId: string, file: File, photoType: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', photoType);

    const token = localStorage.getItem('auth_token');
    const response = await fetch(
      `${API_BASE_URL}/cleaning/sessions/${sessionId}/photos`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    const data: ImageUploadResponse = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as unknown as { error: string }).error || 'Upload failed');
    }
    return data;
  },

  async getPhotos(sessionId: string) {
    return fetchApi<CleaningPhotoResponse>(`/cleaning/sessions/${sessionId}/photos`);
  },

  async deletePhoto(sessionId: string, fileKey: string) {
    return fetchApi<DeleteResponse>(`/cleaning/sessions/${sessionId}/photos?key=${fileKey}`, {
      method: 'DELETE',
    });
  },
};

// Expenses API
export const expensesApi = {
  async list(filters?: {
    property_id?: string;
    category?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return fetchApi<ExpenseListResponse>(`/expenses${queryString ? `?${queryString}` : ''}`);
  },

  async get(id: string) {
    return fetchApi<ExpenseDetailResponse>(`/expenses/${id}`);
  },

  async create(expenseData: ExpenseCreateInput) {
    return fetchApi<ExpenseDetailResponse>('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData),
    });
  },

  async update(id: string, expenseData: ExpenseUpdateInput) {
    return fetchApi<ExpenseDetailResponse>(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/expenses/${id}`, {
      method: 'DELETE',
    });
  },

  async getCategories() {
    return fetchApi<ExpenseCategoriesResponse>('/expenses/categories');
  },
};

// Revenue API
export const revenueApi = {
  async list(filters?: {
    property_id?: string;
    source?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.source) params.append('source', filters.source);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return fetchApi<RevenueListResponse>(`/revenue${queryString ? `?${queryString}` : ''}`);
  },

  async create(revenueData: RevenueCreateInput) {
    return fetchApi<RevenueListResponse>('/revenue', {
      method: 'POST',
      body: JSON.stringify(revenueData),
    });
  },

  async getSummary(filters?: {
    property_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    return fetchApi<RevenueSummaryResponse>(`/revenue/summary${queryString ? `?${queryString}` : ''}`);
  },
};

// Invoices API
export const invoicesApi = {
  async list(filters?: {
    property_id?: string;
    booking_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.booking_id) params.append('booking_id', filters.booking_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return fetchApi<InvoiceListResponse>(`/invoices${queryString ? `?${queryString}` : ''}`);
  },

  async get(id: string) {
    return fetchApi<InvoiceDetailResponse>(`/invoices/${id}`);
  },

  async create(invoiceData: InvoiceCreateInput) {
    return fetchApi<InvoiceDetailResponse>('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
  },

  async update(id: string, invoiceData: InvoiceUpdateInput) {
    return fetchApi<InvoiceDetailResponse>(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/invoices/${id}`, {
      method: 'DELETE',
    });
  },

  async send(id: string) {
    return fetchApi<SuccessResponse>(`/invoices/${id}/send`, {
      method: 'POST',
    });
  },

  async getPayments(id: string) {
    return fetchApi<InvoicePaymentListResponse>(`/invoices/${id}/payments`);
  },

  async recordPayment(id: string, paymentData: InvoicePaymentInput) {
    return fetchApi<SuccessResponse>(`/invoices/${id}/payments`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },
};

// Inventory Catalog API
export const inventoryCatalogApi = {
  async list(filters?: {
    category?: string;
    search?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return fetchApi<InventoryCatalogListResponse>(`/inventory/catalog${queryString ? `?${queryString}` : ''}`);
  },

  async get(id: string) {
    return fetchApi<InventoryCatalogDetailResponse>(`/inventory/catalog/${id}`);
  },

  async create(itemData: InventoryCatalogCreateInput) {
    return fetchApi<InventoryCatalogDetailResponse>('/inventory/catalog', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  },

  async update(id: string, itemData: InventoryCatalogUpdateInput) {
    return fetchApi<InventoryCatalogDetailResponse>(`/inventory/catalog/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/inventory/catalog/${id}`, {
      method: 'DELETE',
    });
  },
};

// Inventory Items API
export const inventoryItemsApi = {
  async list(filters?: {
    property_id?: string;
    category?: string;
    low_stock?: boolean;
    supplier_id?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.low_stock) params.append('low_stock', 'true');
    if (filters?.supplier_id) params.append('supplier_id', filters.supplier_id);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return fetchApi<InventoryItemListResponse>(`/inventory/items${queryString ? `?${queryString}` : ''}`);
  },

  async get(id: string) {
    return fetchApi<InventoryItemDetailResponse>(`/inventory/items/${id}`);
  },

  async create(itemData: InventoryItemCreateInput) {
    return fetchApi<InventoryItemDetailResponse>('/inventory/items', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  },

  async update(id: string, itemData: InventoryItemUpdateInput) {
    return fetchApi<InventoryItemDetailResponse>(`/inventory/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  },

  async delete(id: string) {
    return fetchApi<DeleteResponse>(`/inventory/items/${id}`, {
      method: 'DELETE',
    });
  },

  async adjust(id: string, adjustment: number) {
    return fetchApi<InventoryItemDetailResponse>(`/inventory/items/${id}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ adjustment }),
    });
  },
};

// Guidebook API
export const guidebookApi = {
  async get(propertyId: string) {
    return fetchApi<GuidebookResponse>(`/owner/properties/${propertyId}/guidebook`);
  },

  async create(propertyId: string, guidebookData: GuidebookCreateInput) {
    return fetchApi<GuidebookResponse>(`/owner/properties/${propertyId}/guidebook`, {
      method: 'POST',
      body: JSON.stringify(guidebookData),
    });
  },

  async update(propertyId: string, guidebookData: GuidebookUpdateInput) {
    return fetchApi<GuidebookResponse>(`/owner/properties/${propertyId}/guidebook`, {
      method: 'PUT',
      body: JSON.stringify(guidebookData),
    });
  },

  async getByAccessCode(code: string) {
    return fetchApi<GuidebookResponse>(`/guidebook/${code}`);
  },

  async delete(propertyId: string) {
    return fetchApi<DeleteResponse>(`/owner/properties/${propertyId}/guidebook`, {
      method: 'DELETE',
    });
  },
};

// City Guide Events API (public - no auth required)
export const cityGuideEventsApi = {
  async list(filters?: {
    from?: string;
    to?: string;
    category?: string;
    categories?: string;
    search?: string;
    featured?: boolean;
    section?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.categories) params.append('categories', filters.categories);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.featured) params.append('featured', 'true');
    if (filters?.section) params.append('section', filters.section);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `${API_BASE_URL}/city-guide/events${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to fetch events');
    }

    return data;
  },

  async getToday() {
    const response = await fetch(`${API_BASE_URL}/city-guide/events/today`);
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to fetch today\'s events');
    }
    return data;
  },

  async getUpcoming() {
    const response = await fetch(`${API_BASE_URL}/city-guide/events/upcoming`);
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to fetch upcoming events');
    }
    return data;
  },

  async getById(id: string) {
    const response = await fetch(`${API_BASE_URL}/city-guide/events/${encodeURIComponent(id)}`);
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to fetch event');
    }
    return data;
  },

  /**
   * Absolute URL of the subscribable iCal feed. Calendar apps need a full
   * URL, so resolve API_BASE_URL (which may be a relative '/api') against the
   * current origin. Optional section filter (downtown | fort_bragg | crown).
   */
  getCalendarUrl(section?: string): string {
    const base = `${window.location.origin}${API_BASE_URL}/city-guide/events/calendar.ics`;
    return section ? `${base}?section=${encodeURIComponent(section)}` : base;
  },
};

// City Guide Date Planner API (public - no auth required)
export const datePlannerApi = {
  /** Form config: event types, vibes, budgets, presets, etc. */
  async getConfig() {
    const response = await fetch(`${API_BASE_URL}/city-guide/date-planner/suggestions`);
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to load planner config');
    }
    return data;
  },

  /** Generate a date plan from the selected preferences. */
  async generate(body: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/city-guide/date-planner/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to generate plan');
    }
    return data;
  },

  /** Swap a single stop for an alternative. */
  async swap(body: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/city-guide/date-planner/swap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to swap stop');
    }
    return data;
  },

  /** Save a completed plan. */
  async save(body: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/city-guide/date-planner/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to save plan');
    }
    return data;
  },

  /** Refined venue taxonomy (food/bars/cafes/... with subpills). */
  async getVenueCategories() {
    const response = await fetch(`${API_BASE_URL}/city-guide/date-planner/venue-categories`);
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to load venue categories');
    }
    return data;
  },

  /** Venues for one api_category (e.g. food, drink). */
  async getVenuesByCategory(category: string) {
    const response = await fetch(
      `${API_BASE_URL}/city-guide/date-planner/venues-by-category/${encodeURIComponent(category)}`
    );
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to load venues');
    }
    return data;
  },

  /** Free-text venue search. */
  async searchVenues(q: string) {
    const response = await fetch(
      `${API_BASE_URL}/city-guide/date-planner/venues/search?q=${encodeURIComponent(q)}`
    );
    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, (data as any).error || 'Failed to search venues');
    }
    return data;
  },
};

// Guest Portal API (public - no auth required)
export const guestPortalApi = {
  async getByAccessCode(accessCode: string) {
    // Bypass auth for public endpoint
    const response = await fetch(`${API_BASE_URL}/guest-portal/${accessCode}`);
    const data = await response.json();

    if (!response.ok) {
      const extra: Record<string, unknown> = {};
      if (data.property_name) extra.property_name = data.property_name;
      if (data.property_slug) extra.property_slug = data.property_slug;
      if (data.guest_name) extra.guest_name = data.guest_name;
      throw new ApiError(response.status, data.error || data.message || 'An error occurred', Object.keys(extra).length ? extra : undefined);
    }

    return data;
  },
};

// Access Codes API
export const accessCodesApi = {
  async list(filters?: {
    property_id?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.status) params.append('status', filters.status);

    const queryString = params.toString();
    return fetchApi<AccessCodeListResponse>(`/access-codes${queryString ? `?${queryString}` : ''}`);
  },

  async create(codeData: AccessCodeCreateInput) {
    return fetchApi<AccessCodeCreateResponse>('/access-codes', {
      method: 'POST',
      body: JSON.stringify(codeData),
    });
  },
};

// Recommendations API
export const recommendationsApi = {
  async list(propertyId: string) {
    return fetchApi<RecommendationListResponse>(`/recommendations/${propertyId}`);
  },

  async create(propertyId: string, recommendationData: RecommendationCreateInput) {
    return fetchApi<SuccessResponse>(`/recommendations/${propertyId}`, {
      method: 'POST',
      body: JSON.stringify(recommendationData),
    });
  },
};

// Messages API (Internal Messaging)
export const messagesApi = {
  async list(type: 'inbox' | 'sent' | 'unread' = 'inbox', filters?: {
    property_id?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams({ type });
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    return fetchApi<MessageListResponse>(`/messages?${params.toString()}`);
  },

  async send(messageData: MessageSendInput) {
    return fetchApi<SuccessResponse>('/messages', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  },

  async markAsRead(messageId: string) {
    return fetchApi<SuccessResponse>(`/messages/${messageId}/read`, {
      method: 'PUT',
    });
  },
};

// Message Templates API
export const messageTemplatesApi = {
  async list(filters?: {
    category?: string;
    active?: boolean;
  }) {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());

    const queryString = params.toString();
    return fetchApi<MessageTemplateListResponse>(`/message-templates${queryString ? `?${queryString}` : ''}`);
  },

  async create(templateData: MessageTemplateCreateInput) {
    return fetchApi<SuccessResponse>('/message-templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  },
};

// SMS API
export const smsApi = {
  async send(smsData: SmsSendInput) {
    return fetchApi<SmsSendResponse>('/sms/send', {
      method: 'POST',
      body: JSON.stringify(smsData),
    });
  },
};

// Turnovers API
export const turnoversApi = {
  async list(filters?: {
    property_id?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.property_id) params.append('property_id', filters.property_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);

    const queryString = params.toString();
    return fetchApi<TurnoverListResponse>(`/turnovers${queryString ? `?${queryString}` : ''}`);
  },

  async get(id: string) {
    return fetchApi<TurnoverDetailResponse>(`/turnovers/${id}`);
  },

  async update(id: string, data: TurnoverUpdateInput) {
    return fetchApi<TurnoverDetailResponse>(`/turnovers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

export type { BookingLockCodeReference, BookingLockCodeStatus, BookingStayTimesInput } from '../../shared/booking-lock-codes';
import type { BookingLockCodeReference, BookingLockCodeStatus, BookingStayTimesInput } from '../../shared/booking-lock-codes';

export const bookingLockCodesApi = {
  async get(reference: BookingLockCodeReference): Promise<BookingLockCodeStatus> {
    const query = new URLSearchParams(Object.entries(reference).map(([key, value]) => [key, String(value)]));
    query.set('refresh', '1');
    return fetchApi<BookingLockCodeStatus>(`/owner/booking-lock-code?${query}`);
  },
  async syncTimeZone(input: { calendar_event_id: number; code_id: number; time_zone: string; expected_current_zone?: string }, signal?: AbortSignal): Promise<BookingLockCodeStatus> {
    return fetchApi<BookingLockCodeStatus>('/owner/booking-lock-code', {
      method: 'POST', signal,
      body: JSON.stringify({ action: 'sync_time_zone', calendar_event_id: input.calendar_event_id, code_id: input.code_id, time_zone: input.time_zone,
        ...(input.expected_current_zone ? { expected_current_zone: input.expected_current_zone } : {}) }),
    });
  },
  async adjustStayTimes(input: BookingStayTimesInput, signal?: AbortSignal): Promise<BookingLockCodeStatus> {
    return fetchApi<BookingLockCodeStatus>('/owner/booking-lock-code', {
      method: 'POST', signal, body: JSON.stringify({ action: 'adjust_stay_times', ...input }),
    });
  },
  async issueBackup(calendarEventId: number, signal?: AbortSignal): Promise<BookingLockCodeStatus> {
    return fetchApi<BookingLockCodeStatus>('/owner/booking-lock-code', {
      method: 'POST', signal,
      body: JSON.stringify({ action: 'issue_backup', calendar_event_id: calendarEventId }),
    });
  },
  async setAutomation(propertyId: number | string, enabled: boolean): Promise<{ success: boolean; auto_lock_codes_from_vrbo: boolean }> {
    return fetchApi(`/properties/${propertyId}/smart-locks/config`, {
      method: 'PATCH', body: JSON.stringify({ auto_lock_codes_from_vrbo: enabled }),
    });
  },
};

/** One calendar event as the owner sees it (GET /api/calendar/events/:id). */
export interface CalendarEventDetail {
  id: number;
  property_id?: number | string | null;
  title?: string | null;
  start_date: string;
  end_date: string;
  source?: string | null;
  booking_status?: string | null;
  external_id?: string | null;
  guest_name?: string | null;
  guest_phone?: string | null;
  guest_email?: string | null;
  guest_count?: number | null;
  booking_amount?: number | null;
  /** The owner's chosen door PIN; null means automatic (from the guest phone's last 4 digits). */
  guest_pin?: string | null;
  /** The stay's door PIN: the code on the lock when there is one, else the chosen or automatic one. */
  door_pin?: string | null;
  /** 'lock' = the lock holds a different code than planned (e.g. its first four were taken). */
  door_pin_source?: 'chosen' | 'phone' | 'lock' | null;
  door_has_lock?: boolean;
  /** Where the stay's code stands on the connected lock; null without a lock. */
  door_lock?: 'set' | 'programming' | 'not_set' | null;
  /** Whether OpenBNB puts codes on the lock for this stay by itself. */
  door_auto_codes?: boolean;
}

/** Fields the owner can change on a stay's guest. guest_pin null = automatic. */
export interface CalendarGuestUpdateInput {
  guest_name?: string;
  guest_phone?: string;
  guest_email?: string;
  guest_pin?: string | null;
}

/** 'scheduled': the new PIN is being programmed on the lock, then the old one removed. */
export type LockRekey = 'scheduled' | 'not_needed';

export interface CalendarEventUpdateResponse {
  success: boolean;
  event: any;
  lock_rekey?: LockRekey;
}

/** A confirmed stay inside a Booking.com closure (the block itself stays). */
export interface CreateGuestStayInput {
  guest_name: string;
  guest_phone?: string;
  guest_email?: string;
  start_date: string;
  end_date: string;
  reservation_number?: string;
  /** Omit for an automatic PIN. */
  guest_pin?: string;
}

export interface CreateGuestStayResponse {
  success: true;
  event: CalendarEventDetail;
}

/** Why the server refused a new guest stay with 409. */
export type GuestStayConflictReason = 'overlaps_stay' | 'reservation_exists' | 'not_a_platform_block';

/** One property's status in the Booking.com (or other platform) setup checklist. */
export interface ChannelSetupProperty {
  id: number;
  name: string;
  import_feed: { connected: boolean; last_synced: string | null; sync_status: string | null; sync_error: string | null };
  /** OpenBNB's calendar link for the platform to import; null when the server cannot sign one. */
  export_url: string | null;
  /** The platform's id for this property (Booking.com hotel_id), once linked. */
  platform_listing_id: string | null;
  /** Booking.com only: the property's approved-senders page, once its hotel_id is known. */
  security_settings_url: string | null;
  /** Booking.com only: the property's Contact persons page (who Booking.com emails). */
  contacts_url?: string | null;
}

export interface ChannelSetupStatus {
  platform: 'booking_com' | 'airbnb' | 'vrbo';
  properties: ChannelSetupProperty[];
  inbound: {
    address: string | null;
    status: 'none' | 'active' | 'disabled';
    /** The last email that came from the platform itself (not a test or a forward from elsewhere). */
    last_from_platform: { received_at: string; kind: string | null; outcome: string; reason: string | null } | null;
  };
}

/** The owner's platform email addresses (GET /api/owner/inbound-email). */
export interface InboundAddresses {
  status: 'none' | 'active' | 'disabled';
  addresses: { booking_com: string; airbnb: string; vrbo: string } | null;
  forward_to?: string | null;
}

export const inboundEmailApi = {
  async get(): Promise<InboundAddresses> {
    return fetchApi<InboundAddresses>('/owner/inbound-email');
  },
  async create(): Promise<InboundAddresses> {
    return fetchApi<InboundAddresses>('/owner/inbound-email', { method: 'POST', body: JSON.stringify({ action: 'create' }) });
  },
};

export const channelSetupApi = {
  async get(platform: ChannelSetupStatus['platform']): Promise<ChannelSetupStatus> {
    return fetchApi<ChannelSetupStatus>(`/owner/channel-setup?platform=${platform}`);
  },

  /** Link a property to its Booking.com hotel_id, from the id or any extranet link (only the id is kept). */
  async linkBookingProperty(propertyId: number, hotel: string): Promise<ChannelSetupStatus> {
    return fetchApi<ChannelSetupStatus>('/owner/channel-setup', {
      method: 'POST',
      body: JSON.stringify({ platform: 'booking_com', property_id: propertyId, hotel }),
    });
  },

  /** Mint the owner's inbound addresses (idempotent). */
  async createInboundAddress(): Promise<void> {
    await fetchApi<unknown>('/owner/inbound-email', { method: 'POST', body: JSON.stringify({ action: 'create' }) });
  },
};

// Calendar Events API (individual event CRUD)
export const calendarEventsApi = {
  async get(eventId: number | string): Promise<{ success: boolean; event: CalendarEventDetail }> {
    return fetchApi<{ success: boolean; event: CalendarEventDetail }>(`/calendar/events/${eventId}`);
  },

  async update(eventId: number | string, data: Record<string, any>): Promise<CalendarEventUpdateResponse> {
    return fetchApi<CalendarEventUpdateResponse>(`/calendar/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Turn part of a Booking.com closure into a named guest stay. */
  async createGuestStay(eventId: number | string, body: CreateGuestStayInput): Promise<CreateGuestStayResponse> {
    return fetchApi<CreateGuestStayResponse>(`/calendar/events/${eventId}/guest-stay`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async createAccess(eventId: number | string) {
    return fetchApi<{
      success: boolean;
      access_code: string;
      portal_url: string;
      message: string;
    }>(`/calendar/events/${eventId}/create-access`, {
      method: 'POST',
    });
  },

  async generateLockCode(eventId: number | string) {
    return fetchApi<{
      success: boolean;
      code: string;
      starts_at: string;
      ends_at: string;
      status: string;
      lock_name: string;
      message?: string;
      warning?: string;
    }>(`/calendar/events/${eventId}/lock-code`, {
      method: 'POST',
    });
  },
};

// Smart Locks API
export const smartLocksApi = {
  async list(propertyId: number | string) {
    return fetchApi<{
      success: boolean;
      locks: Array<{
        id: number;
        seam_device_id: string;
        lock_name: string;
        lock_model: string | null;
        lock_brand: string;
        is_online: number;
        battery_level: number | null;
        last_synced: string | null;
        active_codes: number;
      }>;
      lock_code_padding: number;
    }>(`/properties/${propertyId}/smart-locks`);
  },

  async connect(propertyId: number | string) {
    return fetchApi<{
      success: boolean;
      url: string;
      webview_id: string;
    }>(`/properties/${propertyId}/smart-locks/connect`, {
      method: 'POST',
    });
  },

  async available(propertyId: number | string) {
    return fetchApi<{
      success: boolean;
      devices: Array<{
        seam_device_id: string;
        name: string;
        model: string | null;
        brand: string | null;
        online: boolean;
        battery_level: number | null;
        can_program_online: boolean | null;
      }>;
    }>(`/properties/${propertyId}/smart-locks/available`);
  },

  async register(propertyId: number | string, data: { seam_device_id: string; lock_name?: string }) {
    return fetchApi<{ success: boolean; lock: any }>(`/properties/${propertyId}/smart-locks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async remove(propertyId: number | string, lockId: number) {
    return fetchApi<{ success: boolean; message: string }>(`/properties/${propertyId}/smart-locks/${lockId}`, {
      method: 'DELETE',
    });
  },

  async control(propertyId: number | string, lockId: number, action: 'lock' | 'unlock' | 'status') {
    return fetchApi<{
      success: boolean;
      action: string;
      state: {
        is_locked: boolean | null;
        is_online: boolean | null;
        battery_level: number | null;
        can_remotely_lock: boolean;
        can_remotely_unlock: boolean;
      };
    }>(`/properties/${propertyId}/smart-locks/${lockId}`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  },

  async listCodes(propertyId: number | string, lockId: number) {
    return fetchApi<{
      success: boolean;
      codes: Array<{
        id: number;
        code: string;
        code_type: string;
        label: string | null;
        starts_at: string | null;
        ends_at: string | null;
        status: string;
      }>;
    }>(`/properties/${propertyId}/smart-locks/codes?lock_id=${lockId}`);
  },

  async createCode(
    propertyId: number | string,
    data: { lock_id: number; label: string; type: 'permanent' | 'scheduled' | 'onetime'; code?: string; starts_at?: string; ends_at?: string; onetime_hours?: number }
  ) {
    return fetchApi<{ success: boolean; code: string; status: string }>(
      `/properties/${propertyId}/smart-locks/codes`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  },

  async deleteCode(propertyId: number | string, codeId: number) {
    return fetchApi<{ success: boolean }>(
      `/properties/${propertyId}/smart-locks/codes?code_id=${codeId}`,
      { method: 'DELETE' }
    );
  },

  async activity(propertyId: number | string) {
    return fetchApi<{
      success: boolean;
      note: string;
      items: Array<{
        at: string;
        kind: 'action' | 'code';
        door: string | null;
        summary: string;
        detail?: string;
        result?: string;
      }>;
    }>(`/properties/${propertyId}/smart-locks/activity`);
  },

  async getConfig(propertyId: number | string) {
    return fetchApi<{
      success: boolean;
      code_prefix: string;
      code_length: number;
      detected_length: number | null;
    }>(`/properties/${propertyId}/smart-locks/config`);
  },

  async saveConfig(propertyId: number | string, data: { code_prefix: string; code_length: number }) {
    return fetchApi<{ success: boolean; code_prefix: string; code_length: number }>(
      `/properties/${propertyId}/smart-locks/config`,
      { method: 'PATCH', body: JSON.stringify(data) }
    );
  },
};

// Guest Portal Verification API (public - no auth required)
export const guestVerifyApi = {
  async verify(data: { name: string; phone?: string; email?: string }) {
    const response = await fetch(`${API_BASE_URL}/guest-portal/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new ApiError(response.status, (result as any).message || (result as any).error || 'Verification failed');
    }

    return result as {
      success: boolean;
      session_token: string;
      booking_details: {
        guest_name: string;
        check_in_date: string;
        check_out_date: string;
        property_name: string;
      };
    };
  },
};

export const ownerApi = {
  async getStats() {
    return fetchApi<{
      success: boolean;
      stats: {
        pending_requests: number;
        active_stays: number;
        upcoming_checkins_7d: number;
        pending_payment: number;
        revenue: {
          this_month: number;
          this_month_bookings: number;
          ytd: number;
          ytd_bookings: number;
        };
      };
    }>('/owner/stats');
  },
};

export const ownerTasksApi = {
  async list(params?: { status?: string; assigned_to?: string; category?: string }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.assigned_to) qs.set('assigned_to', params.assigned_to);
    if (params?.category) qs.set('category', params.category);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<{ tasks: any[]; count: number }>(`/owner/tasks${query}`);
  },
  async create(data: Record<string, unknown>) {
    return fetchApi<{ task: any }>('/owner/tasks', { method: 'POST', body: JSON.stringify(data) });
  },
  async update(id: number, data: Record<string, unknown>) {
    return fetchApi<{ task: any }>(`/owner/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async remove(id: number) {
    return fetchApi<{ cancelled?: boolean; deleted?: boolean }>(`/owner/tasks/${id}`, { method: 'DELETE' });
  },
  async getMyOwners() {
    return fetchApi<{ owners: Array<{ id: number; email: string; label: string }> }>('/owner/my-owners');
  },
};

export const operationsApi = {
  properties: () => fetchApi<{ properties: OperationsProperty[] }>('/owner/operations/properties'),
  repairSources: (propertyId: number) => fetchApi<{
    repair_requests: Array<{ id: string | number; title: string; status: string; created_at: string }>;
  }>(`/owner/operations/repair-sources?property_id=${propertyId}`),
  invoiceSources: (propertyId: number) => fetchApi<{ invoices: OperationsInvoiceSource[] }>(
    `/owner/operations/invoice-sources?property_id=${propertyId}`),
  invoices: (id: number) => fetchApi<{ invoices: OperationsInvoiceLink[] }>(
    `/owner/operations/${id}/invoices`),
  linkInvoice: (id: number, invoiceId: number) => fetchApi<{ linked: boolean }>(
    `/owner/operations/${id}/invoices`, {
      method: 'POST', body: JSON.stringify({ invoice_id: invoiceId }),
    }),
  list: (propertyId?: number) => fetchApi<{ requests: OperationsRequest[] }>(
    `/owner/operations${propertyId ? `?property_id=${propertyId}` : ''}`),
  detail: (id: number) => fetchApi<{ request: OperationsRequest; role: OperationsRole; events: OperationsEvent[] }>(
    `/owner/operations/${id}`),
  evidence: (id: number) => fetchApi<{ evidence: OperationsAttachment[] }>(
    `/owner/operations/${id}/evidence`),
  checklist: (id: number) => fetchApi<{ items: OperationsChecklistItem[] }>(
    `/owner/operations/${id}/checklist`),
  addChecklistItem: (id: number, title: string) => fetchApi<{ item: OperationsChecklistItem }>(
    `/owner/operations/${id}/checklist`, { method: 'POST', body: JSON.stringify({ title }) }),
  updateChecklistItem: (id: number, itemId: number, action: 'complete' | 'reopen' | 'archive', expectedVersion: number) =>
    fetchApi<{ updated: boolean }>(`/owner/operations/${id}/checklist/${itemId}`, {
      method: 'PATCH', body: JSON.stringify({ action, expected_version: expectedVersion }),
    }),
  uploadEvidence: (id: number, file: File, phase: 'before' | 'after' | '') => {
    const body = new FormData();
    body.append('file', file);
    if (phase) body.append('phase', phase);
    return fetchApi<{ evidence: OperationsAttachment }>(
      `/owner/operations/${id}/evidence`, { method: 'POST', body });
  },
  async downloadEvidence(id: number, attachmentId: number): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_BASE_URL}/owner/operations/${id}/evidence/${attachmentId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(response.status, data.error || 'Could not download evidence');
    }
    return response.blob();
  },
  create: (data: Record<string, unknown>) => fetchApi<{ request: OperationsRequest }>(
    '/owner/operations', { method: 'POST', body: JSON.stringify(data) }),
  transition: (id: number, data: Record<string, unknown>) => fetchApi<{ request: OperationsRequest }>(
    `/owner/operations/${id}/transition`, { method: 'POST', body: JSON.stringify(data) }),
  brief: (propertyId: number) => fetchApi<OperationsBrief>(
    `/owner/operations/brief?property_id=${propertyId}`),
  policy: (propertyId: number) => fetchApi<{ policy: OperationsPolicy; history: OperationsPolicyEvent[] }>(
    `/owner/operations/policy?property_id=${propertyId}`),
  updatePolicy: (data: { property_id: number; delegated_spend_limit_cents: number; expected_version: number }) =>
    fetchApi<{ policy: OperationsPolicy }>('/owner/operations/policy', {
      method: 'PUT', body: JSON.stringify(data),
    }),
  grants: (propertyId: number) => fetchApi<{ grants: OperationsGrant[] }>(
    `/owner/operations/grants?property_id=${propertyId}`),
  grant: (data: { property_id: number; email: string; role: 'chief_of_staff' | 'staff' }) =>
    fetchApi<{ granted: boolean }>('/owner/operations/grants', { method: 'POST', body: JSON.stringify(data) }),
  revoke: (data: { property_id: number; user_id: number }) =>
    fetchApi<{ revoked: boolean }>('/owner/operations/grants', { method: 'DELETE', body: JSON.stringify(data) }),
};

export const servicePricesApi = {
  async list() {
    return fetchApi<{ service_prices: any[] }>('/service-prices');
  },
  async create(data: Record<string, unknown>) {
    return fetchApi<{ service_price: any }>('/service-prices', { method: 'POST', body: JSON.stringify(data) });
  },
  async update(id: string, data: Record<string, unknown>) {
    return fetchApi<{ service_price: any }>(`/service-prices/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async delete(id: string) {
    return fetchApi<{ success: boolean }>(`/service-prices/${id}`, { method: 'DELETE' });
  },
};

// Guest cases: the case file for a guest complaint (functions/api/owner/guest-cases)
export const guestCasesApi = {
  async list(filters: GuestCaseListFilters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const query = params.toString();
    return fetchApi<GuestCaseListResponse>(`/owner/guest-cases${query ? `?${query}` : ''}`);
  },

  async get(caseId: number) {
    return fetchApi<GuestCaseDetail>(`/owner/guest-cases/${caseId}`);
  },

  async create(input: CreateGuestCaseInput) {
    return fetchApi<CreateGuestCaseResponse>('/owner/guest-cases', { method: 'POST', body: JSON.stringify(input) });
  },

  async update(caseId: number, input: UpdateGuestCaseInput) {
    return fetchApi<{ case: GuestCase }>(`/owner/guest-cases/${caseId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async addEvent(caseId: number, input: AddCaseEventInput) {
    return fetchApi<{ event: CaseEvent }>(`/owner/guest-cases/${caseId}/events`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async candidates(caseId: number) {
    return fetchApi<EvidenceCandidatesResponse>(`/owner/guest-cases/${caseId}/candidates`);
  },

  async pin(caseId: number, input: PinEvidenceInput) {
    return fetchApi<{ evidence: CaseEvidence }>(`/owner/guest-cases/${caseId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async unpin(caseId: number, evidenceId: number) {
    return fetchApi<{ unpinned: number }>(`/owner/guest-cases/${caseId}/evidence/${evidenceId}`, { method: 'DELETE' });
  },

  /**
   * Stays across the owner's properties from `days` days ago to today, newest
   * first, for the new-case picker. Blocked dates are left out. There is no
   * cross-property stay search on the server, so this reads each property's
   * calendar; a property that fails is named in failedProperties instead of
   * failing the whole list.
   */
  async recentStays(days: number): Promise<RecentStaysResult> {
    const { properties } = await propertiesApi.list();
    const today = getToday();
    const from = shiftDays(today, -days);
    type ListedEvent = CalendarEventRow & { platform_name?: string | null };

    const settled = await Promise.allSettled(properties.map(async (property) => {
      const { events } = await calendarApi.getEvents(String(property.id), from, today);
      return (events as ListedEvent[])
        .filter((event) => event.booking_status !== BLOCKED_STATUS && Number.isSafeInteger(Number(event.id)))
        .map((event): CaseStayOption => ({
          id: Number(event.id),
          property_id: property.id,
          property_name: property.name ?? property.address,
          guest_name: event.guest_name,
          title: event.title,
          start_date: event.start_date,
          end_date: event.end_date,
          source: event.source,
          platform_name: event.platform_name ?? null,
          external_id: event.external_id,
          booking_status: event.booking_status,
        }));
    }));

    const stays: CaseStayOption[] = [];
    const failedProperties: string[] = [];
    settled.forEach((result, i) => {
      if (result.status === 'fulfilled') stays.push(...result.value);
      else failedProperties.push(properties[i].name ?? properties[i].address);
    });
    stays.sort((a, b) => b.start_date.localeCompare(a.start_date) || b.id - a.id);
    return { stays, failedProperties };
  },

  /** One stay, for a new case opened from a stay elsewhere in the app (?stay=). */
  async stay(stayId: number): Promise<CaseStayOption> {
    const { event } = await fetchApi<StayLookupResponse>(`/calendar/events/${stayId}`);
    return {
      id: Number(event.id),
      property_id: event.property_id,
      property_name: event.property_name ?? 'Property',
      guest_name: event.guest_name,
      title: event.title ?? '',
      start_date: event.start_date,
      end_date: event.end_date,
      source: event.source ?? '',
      platform_name: event.platform_name,
      external_id: event.external_id,
      booking_status: event.booking_status,
    };
  },
};

// Host checkout checks (functions/api/owner/checkout-checks): append-only, so no update or delete
export const checkoutChecksApi = {
  async list(stayId: number) {
    return fetchApi<{ checks: CheckoutCheck[] }>(`/owner/checkout-checks?calendar_event_id=${stayId}`);
  },

  async create(input: CreateCheckoutCheckInput) {
    return fetchApi<CreateCheckoutCheckResponse>('/owner/checkout-checks', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

export { ApiError };
